/**
 * Posting Time Performance — GET /api/v1/report/clients/{id}/heatmap
 * Response rows: { count, hour, period, results?: { new_post_engagement?: { latest_value } } }
 */

const HOURS_PER_DAY = 24

const WEEKDAY_TO_MON_FIRST_INDEX = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
}

function emptyWeeklyGrid() {
  return Array.from({ length: 7 }, () => Array(HOURS_PER_DAY).fill(0))
}

function emptyNullableWeeklyGrid() {
  return Array.from({ length: 7 }, () => Array(HOURS_PER_DAY).fill(null))
}

function periodToMonFirstDayIndex(period) {
  const key = String(period ?? "").trim().toLowerCase()
  if (!key) return null
  return WEEKDAY_TO_MON_FIRST_INDEX[key] ?? null
}

function readPostCount(row) {
  const n = Number(row?.count)
  return Number.isFinite(n) ? n : 0
}

function readPostEngagement(row) {
  const metric = row?.results?.new_post_engagement
  if (!metric || typeof metric !== "object") return null
  const n = Number(metric.latest_value)
  return Number.isFinite(n) ? n : null
}

function hasEngagementData(engagements) {
  return engagements.some((day) =>
    day.some((value) => value != null && Number(value) > 0),
  )
}

function engagementOverlapsSamples(engagements, samples) {
  for (let day = 0; day < engagements.length; day += 1) {
    for (let hour = 0; hour < HOURS_PER_DAY; hour += 1) {
      const posts = Number(samples[day][hour] ?? 0)
      const engagement = Number(engagements[day][hour] ?? 0)
      if (posts > 0 && engagement > 0) return true
    }
  }
  return false
}

function averageEngagementPerPost(engagements, samples) {
  return engagements.map((dayRow, day) =>
    dayRow.map((value, hour) => {
      const engagement = Number(value) || 0
      if (engagement <= 0) return 0
      const posts = Number(samples[day][hour]) || 0
      return posts > 0 ? engagement / posts : engagement
    }),
  )
}

function normalizeMatrixToIntensity(matrix, weights) {
  let maxVal = 0
  for (let day = 0; day < matrix.length; day += 1) {
    for (let hour = 0; hour < (matrix[day]?.length ?? 0); hour += 1) {
      const weight = Number(weights?.[day]?.[hour] ?? matrix[day][hour] ?? 0)
      if (weight > maxVal) maxVal = weight
    }
  }
  if (maxVal <= 0) return matrix

  for (let day = 0; day < matrix.length; day += 1) {
    for (let hour = 0; hour < (matrix[day]?.length ?? 0); hour += 1) {
      const weight = Number(weights?.[day]?.[hour] ?? matrix[day][hour] ?? 0)
      matrix[day][hour] = (weight / maxVal) * 100
    }
  }
  return matrix
}

/**
 * Build OptimalPostingTimesUnifiedWidget `apiPostingHeatmap` from report heatmap rows.
 */
export function buildPostHeatmapFromReportRows(rows, options = {}) {
  const timezone = options.timezone || "UTC"
  const samples = emptyWeeklyGrid()
  const engagements = emptyNullableWeeklyGrid()

  let totalPosts = 0
  let totalEngagements = 0

  for (const row of Array.isArray(rows) ? rows : []) {
    const dayIndex = periodToMonFirstDayIndex(row?.period)
    const hour = Number(row?.hour)
    if (dayIndex == null || !Number.isFinite(hour) || hour < 0 || hour > 23) continue

    const count = readPostCount(row)
    const engagement = readPostEngagement(row)

    samples[dayIndex][hour] += count
    totalPosts += count

    if (engagement != null) {
      engagements[dayIndex][hour] = (engagements[dayIndex][hour] ?? 0) + engagement
      totalEngagements += engagement
    }
  }

  const matrix = samples.map((dayRow) => [...dayRow])

  if (hasEngagementData(engagements) && engagementOverlapsSamples(engagements, samples)) {
    const intensityInput = averageEngagementPerPost(engagements, samples)
    for (let day = 0; day < matrix.length; day += 1) {
      matrix[day] = [...intensityInput[day]]
    }
    normalizeMatrixToIntensity(matrix, intensityInput)
  } else {
    normalizeMatrixToIntensity(matrix, samples)
  }

  return {
    weeklyData: {
      matrix,
      samples,
      engagements,
      timezone,
      totalPostsUsed: totalPosts,
      totalEngagements: totalEngagements > 0 ? totalEngagements : null,
    },
    calendarByDay: {
      dates: [],
      matrix: [],
      samples: [],
      engagements: [],
      hours: [],
      totalPostsUsed: totalPosts,
      totalEngagements: totalEngagements > 0 ? totalEngagements : null,
    },
    monthlyData: {
      dates: [],
      matrix: [],
      samples: [],
      engagements: [],
      hours: [],
      totalPostsUsed: totalPosts,
      totalEngagements: totalEngagements > 0 ? totalEngagements : null,
    },
  }
}
